import { inject, Injectable } from '@angular/core';

import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';

import {
  ComponentWarehouseSourceInput,
  CompoundArticleDetail,
  CompoundArticleCreationInput,
  CompoundArticleCreationResponse,
  CompoundArticleProductionResult,
  CompoundArticleUpdateInput,
  CompoundArticleUpdateResponse,
  GetAllCompoundArticlesResponse,
  GetAllCompoundArticlesVariables,
  WarehouseProductionCapacity,
} from '@shared-models';

import {
  GET_ALL_COMPOUND_ARTICLES,
  GET_COMPOUND_ARTICLE,
  GET_COMPOUND_ARTICLE_PRODUCTION_CAPACITY,
  SEARCH_COMPOUND_ARTICLES,
} from 'src/app/@graphql/operations/query';
import { ApiService } from 'src/app/@graphql/services/api.service';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import {
  CREATE_COMPOUND_ARTICLE,
  PRODUCE_COMPOUND_ARTICLE,
  UPDATE_COMPOUND_ARTICLE,
} from 'src/app/@graphql/operations/mutation';

@Injectable({
  providedIn: 'root',
})
export class CompoundArticlesService extends ApiService {
  private readonly cookieAuthService = inject(CookieAuthService);

  constructor(apollo: Apollo) {
    super(apollo);
  }

  /**
   * Get all compound articles with pagination and ordering
   */
  getAllCompoundArticles(
    page: number,
    size: number,
    orderField: string,
    orderType: string,
  ): Observable<GetAllCompoundArticlesResponse> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company;

    const variables: GetAllCompoundArticlesVariables = {
      company: company?.toString(),
      page,
      size,
      orderField,
      orderType,
    };

    return this.query(GET_ALL_COMPOUND_ARTICLES, variables).pipe(
      map((result: any) => {
        return result.getAllCompoundArticles;
      }),
    );
  }

  searchCompoundArticles(
    value: string,
    id_warehouse: number | null,
    page: number | null,
    size: number | null,
  ): Observable<GetAllCompoundArticlesResponse> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company;

    return this.query(SEARCH_COMPOUND_ARTICLES, {
      company,
      value,
      id_warehouse,
      page,
      size,
    }).pipe(
      map((result: any) => {
        return result.searchCompoundArticles;
      }),
    );
  }

  getCompoundArticle(
    id_compound_article: string,
  ): Observable<CompoundArticleDetail> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company;

    return this.query(GET_COMPOUND_ARTICLE, {
      id_compound_article,
      company: company?.toString(),
    }).pipe(
      map((result: any) => {
        return result.getCompoundArticle;
      }),
    );
  }

  createCompoundArticle(
    compoundArticleInput: CompoundArticleCreationInput,
  ): Observable<CompoundArticleCreationResponse> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company;

    return this.mutation(CREATE_COMPOUND_ARTICLE, {
      ...compoundArticleInput,
      company,
    }).pipe(
      map((result: any) => {
        return result.createCompoundArticle;
      }),
    );
  }

  updateCompoundArticle(
    compoundArticleInput: CompoundArticleUpdateInput,
  ): Observable<CompoundArticleUpdateResponse> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company;

    return this.mutation(UPDATE_COMPOUND_ARTICLE, {
      ...compoundArticleInput,
      company,
    }).pipe(
      map((result: any) => {
        return result.updateCompoundArticle;
      }),
    );
  }

  getCompoundArticleProductionCapacity(
    id_compound_article: string,
    id_warehouse: number,
    component_sources: ComponentWarehouseSourceInput[],
  ): Observable<WarehouseProductionCapacity> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company;

    return this.query(GET_COMPOUND_ARTICLE_PRODUCTION_CAPACITY, {
      id_compound_article,
      id_warehouse,
      component_sources,
      company: company,
    }).pipe(
      map((result: any) => {
        return result.getCompoundArticleProductionCapacity;
      }),
    );
  }

  produceCompoundArticle(
    id_compound_article: string,
    quantity: number,
    target_warehouse: number,
    component_sources: ComponentWarehouseSourceInput[],
  ): Observable<CompoundArticleProductionResult> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company;

    const variables = {
      id_compound_article: parseInt(id_compound_article),
      quantity,
      company,
      target_warehouse,
      component_sources,
    };

    return this.mutation(PRODUCE_COMPOUND_ARTICLE, variables).pipe(
      map((result: any) => {
        return result.produceCompoundArticle;
      }),
    );
  }
}
